import {
  MutationFunction,
  UseMutationOptions,
  UseMutationResult,
  useMutation,
} from "@tanstack/react-query";

import { useAuthentication } from "@/lib/contexts";
import { useMaintenanceMode } from "@/lib/contexts";
import { CustomError } from "../../errors";

interface MutationWrapperProps<TData, TVariables> {
  mutationKey: string[];
  captureUnauthorizedError?: boolean;
  mutationFn: MutationFunction<TData, TVariables>;
  onSuccess: (data: TData, variables: TVariables) => void;
  onError?: (error: Error) => void;
}

export function useMutationWrapper<TData, TVariables = void>({
  mutationKey,
  captureUnauthorizedError = true,
  mutationFn,
  onError,
  onSuccess,
}: MutationWrapperProps<TData, TVariables>): UseMutationResult<TData, Error, TVariables> {
  const { removeUser } = useAuthentication();
  const { setMaintenanceMode, isMaintenanceMode } = useMaintenanceMode();

  const handleError = (error: Error) => {
    if (error instanceof CustomError) {
      if (captureUnauthorizedError && error.statusCode === 401) {
        removeUser();
        return;
      }

      if (error.statusCode === 503) {
        setMaintenanceMode(true);
        return;
      }
    }

    onError?.(error);
  };

  const mutationOptions: UseMutationOptions<TData, Error, TVariables> = {
    mutationKey,
    mutationFn,
    onSuccess: (data, variables) => {
      // If mutation succeeds, we're back online
      isMaintenanceMode && setMaintenanceMode(false);
      onSuccess(data, variables);
    },
    onError: handleError,
  };

  const mutationResult = useMutation(mutationOptions);

  return mutationResult;
}
